import React, { FC } from "react";
import { Badge, Card, Heading, Text } from "@telia/teddy";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import { CopyButton } from "../../../components/atoms/CopyButton";
import { getSalesAgents } from "../../../state/selectors/selectors";
import { OrderDetails } from "../../../types/models";
import { contractStatusText, contractStatusVariant } from "../contractStatusUtils";
import { getStatusText, statusVariant } from "../orderStatusUtils";

interface OrderSummarySectionProps {
    order: OrderDetails;
}

const formatCreationDate = (creationDate?: number): string => {
    if (!creationDate) return "";
    const date = new Date(creationDate);
    return isNaN(date.getTime()) ? "" : format(date, "dd.MM.yyyy HH:mm");
};

const formatPaymentAmount = (amount: string): string => {
    const parsed = Number(amount);
    return isNaN(parsed) ? amount : parsed.toLocaleString("nb-NO");
};

const OrderSummarySection: FC<OrderSummarySectionProps> = ({ order }) => {
    const salesAgents = useSelector(getSalesAgents);
    const agent = salesAgents.find((a) => String(a.id) === order.salesAgent);
    const agentName = agent ? `${agent.firstName} ${agent.lastName}`.trim() : order.salesAgent || "Ikke angitt";

    const hasUpfrontPayment = !!order.totalUpFrontPaymentAmount && Number(order.totalUpFrontPaymentAmount) > 0;

    return (
        <section className="order-detail__section order-detail__summary">
            <div className="order-detail__summary-header">
                <Heading mb="100" as="h1" variant="title-300">
                    Ordredetaljer #{order.orderId}
                </Heading>
                <CopyButton
                    value={String(order.orderId)}
                    ariaLabel="Kopier ordre-ID"
                    toastMessage="Ordre-ID kopiert"
                    tooltipLabel="Kopier ordre-ID"
                />
            </div>
            <Text variant="additional-100" mb="300">
                Opprettet {formatCreationDate(order.creationDate)}
            </Text>
            <Card variant="white" bordered className="order-detail__summary-card">
                <Card.Content>
                    <div className="order-detail__summary-rows">
                        <Text variant="additional-100">Status</Text>
                        <Badge
                            className="order-detail__summary-badge"
                            variant={statusVariant(order.orderState)}
                            hideIcon
                        >
                            {getStatusText(order.orderState)}
                        </Badge>

                        <Text variant="additional-100">Kontrakt</Text>
                        <Badge
                            className="order-detail__summary-badge"
                            variant={contractStatusVariant(order.contractSignStatus)}
                            hideIcon
                        >
                            {contractStatusText(order.contractSignStatus)}
                        </Badge>

                        <Text variant="additional-100">Betaling</Text>
                        {hasUpfrontPayment ? (
                            <Badge className="order-detail__summary-badge" variant="success" hideIcon>
                                Betalt (kr {formatPaymentAmount(order.totalUpFrontPaymentAmount as string)})
                            </Badge>
                        ) : (
                            <Badge className="order-detail__summary-badge" variant="information" hideIcon>
                                Faktura sendes i posten
                            </Badge>
                        )}

                        <Text variant="additional-100">Selger</Text>
                        <Text data-di-mask>{agentName}</Text>
                    </div>
                </Card.Content>
            </Card>
        </section>
    );
};

export default OrderSummarySection;
